import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchOperationsData } from '../../store/slices/operationsSlice';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Modal } from '../../components/ui/modal';
import TransactionReceipt from '../../components/transactions/TransactionReceipt';
import { PROVIDERS } from '../../lib/constants';
import { Method, Packet, operationsService } from '../../services/api/operationsService';
import { addTransaction } from '../../store/slices/transactionsSlice';
import { printService } from '../../services/printService';
import { Transaction } from '../../types';

const STORAGE_BASE = 'https://kaoxet.net/storage/';
const fmt = (n: number) =>
  new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 }).format(n);

const PaquetesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { wallet }        = useAppSelector((state) => state.wallet as any);
  const { packetMethods } = useAppSelector((state) => state.operations as any);
  const fetchedRef = useRef(false);

  const [phoneNumber,       setPhoneNumber]      = useState('');
  const [selectedProvider,  setSelectedProvider] = useState('');
  const [packets,           setPackets]          = useState<Packet[]>([]);
  const [loadingPackets,    setLoadingPackets]   = useState(false);
  const [selectedPacket,    setSelectedPacket]   = useState<Packet | null>(null);
  const [isProcessing,      setIsProcessing]     = useState(false);
  const [error,             setError]            = useState<string | null>(null);
  const [success,           setSuccess]          = useState<string | null>(null);
  const [showReceiptModal,  setShowReceiptModal]  = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState<Transaction | null>(null);

  useEffect(() => {
    if (!fetchedRef.current && (!packetMethods || packetMethods.length === 0)) {
      fetchedRef.current = true;
      dispatch(fetchOperationsData());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const providerList: Method[] =
    packetMethods && packetMethods.length > 0 ? packetMethods : PROVIDERS.PAQUETES;

  // Consulta paquetes cuando hay teléfono + proveedor
  const fetchPackets = async (phone: string, provider: string) => {
    if (!phone || phone.length < 10 || !provider) return;
    setLoadingPackets(true);
    setPackets([]);
    setSelectedPacket(null);
    setError(null);
    try {
      const res = await operationsService.fetchPackets(phone, provider);
      if (!res.success) throw new Error(res.message || 'Error al consultar paquetes');
      setPackets(res.data || []);
      if ((res.data || []).length === 0) setError('No hay paquetes disponibles para este número y operador');
    } catch (err: any) {
      setError(err.message || 'Error al consultar paquetes');
    } finally {
      setLoadingPackets(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '');
    if (v.length <= 10) {
      setPhoneNumber(v);
      setPackets([]); setSelectedPacket(null);
      if (v.length === 10 && selectedProvider) fetchPackets(v, selectedProvider);
    }
  };

  const handleProviderSelect = (id: string) => {
    setSelectedProvider(id);
    setPackets([]); setSelectedPacket(null); setError(null);
    if (phoneNumber.length === 10) fetchPackets(phoneNumber, id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber || phoneNumber.length < 10) { setError('Ingrese un número de teléfono válido de 10 dígitos'); return; }
    if (!selectedProvider) { setError('Seleccione una compañía'); return; }
    if (!selectedPacket)   { setError('Seleccione un paquete'); return; }
    const price = Number(selectedPacket.precio);
    if (wallet && wallet.balance < price) { setError('Saldo insuficiente para comprar este paquete'); return; }

    setIsProcessing(true); setError(null);
    try {
      const res = await operationsService.sendPacket(
        phoneNumber, selectedProvider, selectedPacket.id, price, selectedPacket.nombre
      );
      if (!res.success) throw new Error(res.message || 'Error al procesar el paquete');

      const providerName = providerList.find(p => p.id === selectedProvider)?.name || selectedProvider;
      const transaction: any = {
        id:        res.data?.xid || res.data?.transactionId || `pkg-${Date.now()}`,
        xid:       res.data?.xid || res.data?.transactionId || undefined,
        type:      'PAQUETE' as const,
        amount:    price,
        provider:  providerName,
        date:      new Date().toISOString(),
        status:    'COMPLETED' as const,
        reference: Math.random().toString(36).substring(2, 10).toUpperCase(),
        url:       res.data?.url || null,
        details:   { phoneNumber, provider: providerName, packageName: selectedPacket.nombre, price },
      };
      dispatch(addTransaction(transaction));
      setSuccess(`Paquete "${selectedPacket.nombre}" activado con éxito al número ${phoneNumber}`);
      setCurrentTransaction(transaction);
      setShowReceiptModal(true);
      setPhoneNumber(''); setSelectedProvider(''); setSelectedPacket(null); setPackets([]);
    } catch (err: any) {
      setError(err.message || 'Error al procesar el paquete. Intente nuevamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <svg className="w-6 h-6 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"/>
          </svg>
          Paquetes
        </h1>
        <p className="text-slate-500 text-sm mt-1">Active paquetes de datos, minutos y SMS.</p>
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
          </svg>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4 mb-5">
          <svg className="w-5 h-5 text-green-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
          </svg>
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Compañía */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Compañía</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {providerList.map((p: Method) => (
              <button key={p.id} type="button" onClick={() => handleProviderSelect(p.id)}
                className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all duration-150 hover:border-violet-300"
                style={{
                  borderColor: selectedProvider === p.id ? '#7c3aed' : '#e2e8f0',
                  background:  selectedProvider === p.id ? '#f5f3ff' : '#fff',
                }}>
                <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center overflow-hidden">
                  {p.logo
                    ? <img src={`${STORAGE_BASE}${p.logo}`} alt={p.name} className="w-10 h-10 object-contain"/>
                    : <span className="text-lg font-bold text-slate-400">{p.name.charAt(0)}</span>
                  }
                </div>
                <span className="text-xs font-medium text-slate-600 text-center leading-tight">{p.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Teléfono */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Número de teléfono</p>
          <Input
            label="Teléfono (10 dígitos)"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder="8091234567" type="tel" fullWidth leftAddon="+1"
            useCustomKeyboard={false}
          />
          {phoneNumber.length === 10 && selectedProvider && loadingPackets && (
            <div className="flex items-center gap-2 mt-3 text-slate-400 text-sm">
              <svg className="animate-spin w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              Consultando paquetes disponibles…
            </div>
          )}
        </div>

        {/* Paquetes */}
        {packets.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Paquetes disponibles</p>
            <div className="space-y-2">
              {packets.map(pkg => (
                <label key={pkg.id}
                  className="flex items-center justify-between gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 hover:border-violet-300"
                  style={{
                    borderColor: selectedPacket?.id === pkg.id ? '#7c3aed' : '#e2e8f0',
                    background:  selectedPacket?.id === pkg.id ? '#f5f3ff' : '#fff',
                  }}>
                  <input type="radio" name="packet" className="sr-only"
                    checked={selectedPacket?.id === pkg.id}
                    onChange={() => { setSelectedPacket(pkg); setError(null); }}/>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-800">{pkg.nombre}</p>
                    {pkg.descripcion && <p className="text-xs text-slate-500 mt-0.5">{pkg.descripcion}</p>}
                  </div>
                  <span className="text-lg font-black text-violet-600 shrink-0">{fmt(Number(pkg.precio))}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Resumen */}
        {selectedPacket && (
          <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-violet-700 font-medium">{selectedPacket.nombre}</p>
              <p className="text-xs text-violet-500">
                {providerList.find(p => p.id === selectedProvider)?.name} · +1 {phoneNumber}
              </p>
            </div>
            <span className="text-2xl font-black text-violet-600">{fmt(Number(selectedPacket.precio))}</span>
          </div>
        )}

        <Button type="submit" size="touch" fullWidth isLoading={isProcessing}
          disabled={!phoneNumber || phoneNumber.length < 10 || !selectedProvider || !selectedPacket}>
          {isProcessing ? 'Procesando...' : '✓ Comprar Paquete'}
        </Button>
      </form>

      <Modal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)}
        title="Resumen de Transacción" size="md">
        {currentTransaction && (
          <TransactionReceipt transaction={currentTransaction}
            onClose={() => setShowReceiptModal(false)}
            onPrint={() => { if (currentTransaction) printService.printTicket(currentTransaction); }}
          />
        )}
      </Modal>
    </div>
  );
};

export default PaquetesPage;
